import { useSelector } from 'react-redux';
import { debounce, difference } from 'lodash';
import { FormInput } from '@praktio/common-ui';
import { Link, useLocation } from 'react-router-dom';
import { FC, useEffect, useRef, useState } from 'react';

import CLEs from './CLEs';
import FormHeader from './FormHeader/FormHeader';
import FormSection from './FormSection/FormSection';
import ReportNotification from './ReportNotification';
import ActiveCourses from './ActiveCourses/ActiveCourses';
import ActiveTeamsTree from './ActiveTeamsTree/ActiveTeamsTree';
import ShowHidePasswordButton from './ShowHidePasswordButton/ShowHidePasswordButton';

import { RootState } from '../../ducks/store';
import { isPlatformAdmin } from '../../utils/functions';
import { useGetCourseNames } from '../../hooks/useGetCourseNames';
import { useGetTeamCourses } from '../../hooks/useGetTeamCourses';
import { getCourseIdsFromCourseNames, toggleSelection } from '../../utils/reports';
import { Roles, IUserFields, SSOPasswordNotice, DEBOUNCE_DELAY } from '../../utils/constants';

import './User.scss';

interface IProps {
  user: IUser;
  userFormErrors?: IBootstrapFormErrors;
  isNew?: boolean;
  teamId?: string;
  teamName?: string;
  cleRequired?: boolean;
  handleFormEdit: (field: string, value: string | boolean | ICLEEntry | string[]) => void;
  hideRoleSpecificFields?: boolean;
}

const PasswordSection: FC<{
  user?: IUser;
  handleFormEdit: (field: string, value: string | boolean | ICLEEntry | string[]) => void;
  userFormErrors?: IBootstrapFormErrors;
}> = ({ user, handleFormEdit, userFormErrors }) => {
  const location = useLocation();
  const isRegistration = !!location?.pathname.match(/^\/register$/gi);
  const isSettings = !!location?.pathname.match(/\/settings$/gi);
  const [showPassword, setShowPassword] = useState(false);

  if (!user || user.forceRegistration) {
    return null;
  }

  const { ssoData, isSSOSession, passwordSet, forcePasswordReset } = user;

  // User is an SSO Account
  const requirePassword = isRegistration || (isSettings && forcePasswordReset);
  const isSSOUser = !!ssoData;
  const passwordInputType = showPassword ? 'text' : 'password';
  const passwordLabelText = `${!isRegistration ? (!passwordSet ? 'Set ' : 'Update ') : ''}Password`;
  const showCurrentPasswordField = isSettings && !isSSOSession;
  return (
    <FormSection
      subheader={`${!isRegistration ? `${passwordSet ? 'Res' : 'S'}et ` : ''}Password`}
      info={isSSOUser ? SSOPasswordNotice : ''}
    >
      <div>
        {!!showCurrentPasswordField && (
          <>
            <FormInput
              value={user?.currentPassword}
              label="Current Password"
              placeholder="Current Password"
              fieldName="currentPassword"
              type={passwordInputType}
              onChange={(e: any) => handleFormEdit('currentPassword', e.target.value)}
              error={userFormErrors?.currentPassword}
              required={requirePassword}
              labelWrapperClassName="label-show-hide-group d-flex justify-content-between"
              labelAdditions={
                <ShowHidePasswordButton
                  clickFunction={() => setShowPassword(!showPassword)}
                  passwordVisible={showPassword}
                  passwordFieldType={passwordInputType}
                />
              }
              lastInSection
              noTrimming
            />
            <div className="mt-1 mb-3 forgot-password">
              <Link to="/forgot">Forgot password?</Link>
            </div>
          </>
        )}
        <FormInput
          value={user?.password}
          label={passwordLabelText}
          placeholder={passwordLabelText}
          fieldName="password"
          type={passwordInputType}
          onChange={(e: any) => handleFormEdit('password', e.target.value)}
          error={userFormErrors?.password}
          required={requirePassword}
          labelWrapperClassName="label-show-hide-group d-flex justify-content-between"
          labelAdditions={
            !showCurrentPasswordField ? (
              <ShowHidePasswordButton
                clickFunction={() => setShowPassword(!showPassword)}
                passwordVisible={showPassword}
                passwordFieldType={passwordInputType}
              />
            ) : undefined
          }
          noTrimming
        />
        <FormInput
          value={user?.confirmPassword}
          label="Confirm Password"
          placeholder="Confirm Password"
          fieldName="confirmPassword"
          type={passwordInputType}
          onChange={(e: any) => handleFormEdit('confirmPassword', e.target.value)}
          error={userFormErrors?.confirmPassword}
          required={requirePassword}
          noTrimming
        />
      </div>
    </FormSection>
  );
};

// TODO: Some of the deselectedCourses logic is also in ReportSettingsPanel.tsx. Maybe we should figure out a way to extract it into a single place in the future.
const User: FC<IProps> = ({
  user,
  userFormErrors,
  isNew,
  teamId,
  teamName,
  cleRequired,
  handleFormEdit,
  hideRoleSpecificFields,
}) => {
  const initialFetchFinished = useRef(false);

  // ===========================================================================
  // Hooks
  // ===========================================================================

  const { getTeamsCourseIds } = useGetTeamCourses();
  const { fetchCourses, courses, error, isLoading, isFetching } = useGetCourseNames();

  const { isAdmin } = useSelector((state: RootState) => {
    const user = state.auth.authenticatedUser;
    const isAdmin = isPlatformAdmin(user);

    return {
      isAdmin,
    };
  });

  // ===========================================================================
  // State
  // ===========================================================================

  const [currentTeamCourseIdsLocal, setCurrentTeamCourseIdsLocal] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedTeamsPrev, setSelectedTeamsPrev] = useState<string[]>([]);
  const [teamSelectionLoading, setTeamSelectionLoading] = useState(false); // Used to show loading spinner when selecting teams

  const [deselectedCourses, setDeselectedCourses] = useState<string[]>([]);

  // ===========================================================================
  // Other
  // ===========================================================================

  const { cles, roles } = user || {};
  const isLoadingOrFetching = isLoading || isFetching;
  const hasTeamManagerRole = !!roles?.find((r) => r.role === Roles.TeamManager);

  const noCoursesSelected = currentTeamCourseIdsLocal.every((courseId) =>
    deselectedCourses.includes(courseId),
  );

  // ===========================================================================
  // Debounced Functions
  // - We use these debounced functions to reduce the number of requests made
  //   to the server to get report data.
  // ===========================================================================

  const debouncedFetchCourses = useRef(
    debounce((teamIds) => {
      fetchCourses({ teamIds }).then(({ data: courses }) => {
        const courseIds = getCourseIdsFromCourseNames(courses);

        setCurrentTeamCourseIdsLocal(courseIds);
        setTeamSelectionLoading(false);
      });
    }, DEBOUNCE_DELAY),
  ).current;

  // ===========================================================================
  // Functions
  // ===========================================================================

  const autoSelectTeamCourses = async (selectedTeams: string[]) => {
    setTeamSelectionLoading(true);

    // If no teams are selected, clear selected courses
    if (!selectedTeams.length) {
      setDeselectedCourses([]);
      return;
    }

    const addedTeams = difference(selectedTeams, selectedTeamsPrev); // Teams that were added
    const newTeamCoursesIds = getTeamsCourseIds(addedTeams); // Course IDs for newly selected teams only
    const newDeselectedCourses = difference(deselectedCourses, newTeamCoursesIds); // Remove IDs from deselected courses if they are in the new team courses

    setDeselectedCourses(newDeselectedCourses);
    setSelectedTeamsPrev(selectedTeams);
  };

  const handleTeamsSelectionChange = (selectedIds: string[]) => {
    setSelectedTeams(selectedIds);
    handleFormEdit('reportTeams', selectedIds);
  };

  const handleClearAllCourses = () => {
    const allCourseIds = getCourseIdsFromCourseNames(courses);
    setDeselectedCourses(allCourseIds);
  };

  const handleSelectAllCourses = () => setDeselectedCourses([]);

  const handleCourseClick = (courseId: string) => {
    setDeselectedCourses((prev) => toggleSelection(prev, courseId));
  };

  const onNotificationChange = (value?: string, prop?: string) => {
    if (prop && handleFormEdit && value) {
      handleFormEdit(prop, value);
    }
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  // Set user state and Fetch courses on initial load, only when user is loaded
  useEffect(() => {
    if (initialFetchFinished.current || !user || !user?.reportTeams) {
      return;
    }

    if (user?.reportDeselectedCourses) {
      setDeselectedCourses(user?.reportDeselectedCourses);
    }

    if (user?.reportTeams) {
      setSelectedTeams(user?.reportTeams);
      setSelectedTeamsPrev(user?.reportTeams);
    }

    debouncedFetchCourses(user?.reportTeams);

    initialFetchFinished.current = true;

    // We only want to fetch courses once when the user is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch courses when the selected teams change (only for admins)
  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    autoSelectTeamCourses(selectedTeams);
    debouncedFetchCourses(selectedTeams);

    // We only want to fetch courses when the selected teams change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeams]);

  // Update deselected courses in the parent component (only for admins)
  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    handleFormEdit('reportDeselectedCourses', deselectedCourses);

    // We only want to update deselected courses when the deselected courses change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deselectedCourses]);

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <div className="CommonUser">
      <div>
        {!!teamId && !!isNew && (
          <div>
            New user for Team '<Link to={`/admin/teams/${teamId}`}>{teamName}</Link>'
          </div>
        )}

        <FormSection subheader="Personal Information">
          <FormInput
            value={user?.email}
            label="Email"
            id="Email"
            onChange={(e: any) => handleFormEdit('email', e.target.value)}
            error={userFormErrors?.email}
            required
            type="text"
          />
          <FormInput
            value={user?.firstName}
            label="First Name"
            id="FirstName"
            onChange={(e: any) => handleFormEdit('firstName', e.target.value)}
            error={userFormErrors?.firstName}
            required
          />
          <FormInput
            value={user?.lastName}
            label="Last Name"
            id="LastName"
            onChange={(e: any) => handleFormEdit('lastName', e.target.value)}
            error={userFormErrors?.lastName}
            required
          />
          <FormInput
            value={user?.practiceGroup}
            label="Practice Group/Department"
            placeholder="For example: M&A"
            id="PracticeGroup"
            onChange={(e: any) => handleFormEdit('practiceGroup', e.target.value)}
            error={userFormErrors?.practiceGroup}
          />
          <FormInput
            value={user?.firmOffice}
            label="Office Location"
            placeholder="For example: New York"
            id="OfficeLocation"
            onChange={(e: any) => handleFormEdit('firmOffice', e.target.value)}
            error={userFormErrors?.firmOffice}
          />
          <FormInput
            value={user?.position}
            label="Current Position"
            placeholder="For example: Associate"
            id="CurrentPosition"
            onChange={(e: any) => handleFormEdit('position', e.target.value)}
            error={userFormErrors?.position}
          />
        </FormSection>
        <FormSection subheader="CLE">
          <FormInput
            label={`Are you seeking CLE?${cleRequired ? ' *' : ''}`}
            type="radio"
            fieldName="optOutCLEs"
            inlineChoices
            value={
              user?.optOutCLEs === true ? 'true' : user?.optOutCLEs === false ? 'false' : undefined
            }
            onChange={(event: any) =>
              handleFormEdit(
                IUserFields.OptOutCLEs,
                (event?.target.value).toLowerCase() === 'true'.toLowerCase() ? true : false,
              )
            }
            radioChoices={[
              { label: 'Yes', value: 'false' },
              { label: 'No', value: 'true' },
            ]}
            error={userFormErrors?.cles}
            labelClassName="cle-radio"
          />
          {user?.optOutCLEs === false && <CLEs cles={cles} handleFormEdit={handleFormEdit} />}
        </FormSection>
        <PasswordSection
          user={user}
          handleFormEdit={handleFormEdit}
          userFormErrors={userFormErrors}
        />
        {!!hasTeamManagerRole && !hideRoleSpecificFields && (
          <>
            <FormHeader formName="Report Settings" />

            <ActiveTeamsTree
              noDefault
              isSearchable
              teams={user?.teams}
              activeTeams={user?.reportTeams}
              error={userFormErrors?.reportTeams}
              onSelectedChange={handleTeamsSelectionChange}
            />

            {!!isAdmin && (
              <ActiveCourses
                noTooltip
                courses={courses}
                deselectedCourses={user?.reportDeselectedCourses}
                noCoursesSelected={noCoursesSelected}
                error={error as any}
                isLoading={isLoadingOrFetching || teamSelectionLoading}
                formError={userFormErrors?.reportDeselectedCourses}
                onClearSelectClick={
                  noCoursesSelected ? handleSelectAllCourses : handleClearAllCourses
                }
                onCourseClick={handleCourseClick}
              />
            )}

            <ReportNotification
              notification={user?.reportNotification}
              onChange={onNotificationChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default User;
